import React, { CSSProperties } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import classNames from 'classnames';
import tinycolor from 'tinycolor2';

import { Appointment, Event, renderRosterNote } from '~weekly-planner/lib/common';

import { TooltipPortal } from '~components';
import Icon, {
  faArrowRight,
  faBan,
  faCalendar,
  faCar,
  faCommentDollar,
  faDollarSign,
  faLock,
  faPersonCircleExclamation,
  faPersonWalkingArrowRight,
} from '~components/icon';
import IconStack from '~components/IconStack';

import { openAppointmentDetails } from '~weekly-planner/reducers/weeklyPlanner';

import { selectServiceTypeColourById } from '~appointments/selectors/serviceTypes';
import { selectOptions } from '~weekly-planner/selectors';
import { selectChangeById } from '~weekly-planner/selectors/appointments';

import { WorkerQualifications } from '../common';
import StatusStrip from './StatusStrip';

interface ComponentProps {
  appointment: Appointment;
  events: Event[];
  contextTarget: string;
  overrideValues?: any;
  className?: string;
  style?: CSSProperties;
  getTitle: (appointment: Appointment) => string;
  isDashboardView?: boolean;
}

const AppointmentCard: React.FC<ComponentProps> = ({
  appointment,
  overrideValues,
  className,
  style,
  getTitle,
  isDashboardView,
  contextTarget,
}) => {
  const dispatch = useAppDispatch();
  const shouldOverride = overrideValues && overrideValues.id === appointment.id;

  const displayAppointment = {
    ...appointment,
    ...(shouldOverride ? overrideValues : {}),
  };

  const {
    key,
    id,
    start_time,
    end_time,
    status,
    flexibility,
    service_type,
    is_travel_exempt,
    is_cancelled,
    is_active_cancellation,
    is_leave_entry,
    is_shift_pay,
    is_fixed_pay,
    is_on_hold,
    is_unallocated,
    is_overnight,
    is_second_day,
    qualification_level,
    notes_admin,
  } = displayAppointment;

  const hasChanged = useAppSelector((state) => selectChangeById(state, key));
  const serviceTypeColour = useAppSelector((state) => selectServiceTypeColourById(state, service_type?.id));
  const { appointmentFlexibility, showServiceType, showCardQualifications } = useAppSelector(selectOptions);

  const title = getTitle(appointment);

  const locationId = `appointment-card-${key}`;

  const backgroundColor = serviceTypeColour ? serviceTypeColour : 'blue';
  const appointmentCardStyle = {
    '--card-background-colour': backgroundColor,
    '--card-text-colour': tinycolor(backgroundColor).isDark() ? 'white' : undefined,
    ...style,
  } as React.CSSProperties;

  const cardClassName = classNames('appointment-card', {
    [`${className}`]: className !== undefined,
    changed: hasChanged,
    cancelled: is_cancelled,
    new: id === null,
    flexible: appointmentFlexibility && flexibility,
    'non-flexible': appointmentFlexibility && !flexibility,
    'shift-pay': is_shift_pay,
    'on-hold': is_on_hold,
    'is-unallocated': is_unallocated,
    'split-overnight': is_overnight,
    'first-half': is_second_day === false && is_overnight,
    'second-half': is_second_day,
  });

  const buildStatusIcons = () => {
    const iconRow = [];

    if (is_travel_exempt) {
      iconRow.push(
        <IconStack
          key="is_travel_exempt"
          id="is_travel_exempt"
          title="Travel exempt"
          icons={[
            { icon: faCar, size: 'xs' },
            { icon: faBan, size: 'lg' },
          ]}
        />,
      );
    }
    if (is_active_cancellation) {
      iconRow.push(<Icon key="is_active_cancellation" icon={faCommentDollar} title="Active Cancellation" />);
    }
    if (is_leave_entry) {
      iconRow.push(<Icon key="is_leave_entry" icon={faPersonWalkingArrowRight} title="Leave Entry" />);
    }
    if (is_shift_pay) {
      iconRow.push(
        <IconStack
          key="is_shift_pay"
          id="is_shift_pay"
          title="Shift Pay"
          icons={[
            { icon: faCalendar, size: 'lg' },
            { icon: faDollarSign, size: 'xs', transform: 'down-4', style: { color: 'white' } },
          ]}
        />,
      );
    }
    if (is_fixed_pay) {
      iconRow.push(<Icon key="is_fixed_pay" icon={faLock} title="Fixed Pay" />);
    }
    if (notes_admin && id !== null) {
      iconRow.push(
        <span key="notes_admin" className="roster-note-icon">{renderRosterNote({ id: id as number, notes_admin })}</span>,
      );
    }

    return iconRow;
  };

  const unallocatedTitle = (
    <span className="unallocated-title">
      <div>
        <span className="unallocated-text">NO WORKER ASSIGNED</span>
      </div>
      <div className="unallocated-icon">
        <Icon icon={faPersonCircleExclamation} title="Unallocated Appointment" size="xl" color="black" />
      </div>
    </span>
  );

  return (
    <>
      <div
        id={contextTarget}
        className={cardClassName}
        style={appointmentCardStyle}
        onClick={(e) => {
          isDashboardView ? dispatch(openAppointmentDetails(id)) : e.stopPropagation();
        }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <TooltipPortal targetId={contextTarget} content={`${title} (${start_time} - ${end_time})`} />
        <StatusStrip status={status} changed={hasChanged} />
        <div className="appointment-info">
          <div>
            <strong>{is_unallocated ? unallocatedTitle : title}</strong>
          </div>
          {showServiceType && service_type && <div>{service_type.name ?? ''}</div>}
          <div>
            {start_time} - {end_time}
          </div>
          {showCardQualifications && <WorkerQualifications qualifications={qualification_level} isCondensed />}
          <div className="appointment-status-icons">{buildStatusIcons()}</div>
        </div>
      </div>
      {is_second_day && (
        <TooltipPortal
          targetId={contextTarget}
          locationId={locationId}
          content={
            <span>
              To move, drag the start of the appointment <Icon icon={faArrowRight} />
            </span>
          }
          placement="left"
        />
      )}
    </>
  );
};

export default AppointmentCard;
